/**
 * <ReportChapterRow> — one chapter worked on during the session (molecule, L0.1).
 *
 * The chapter's subject icon + name (colored by subject), its subject subtitle and an activity-level
 * `<Badge>` ("Révision du cours" / "Entraînement" / "Approfondissement"), then the per-chapter
 * "+N travail(aux) envoyé(s)" / "+N travail(aux) à faire" counters (as `<Banner>`s) and the mastery
 * `<FlagProgressBar>` (XP gained fill + a flag planted at the assigned milestone, accent while work
 * remains). Composes DS atoms only. No hex.
 */
import React from "react"
import { StyleSheet } from "react-native-unistyles"

import { Badge } from "../../atoms/Badge"
import { Banner } from "../../atoms/Banner"
import { FlagProgressBar } from "../../atoms/FlagProgressBar"
import { Row } from "../../atoms/containers/Row"
import { Stack } from "../../atoms/containers/Stack"
import { ImageIcon } from "../../atoms/icons/ImageIcon"
import { Text } from "../../atoms/typography/Text"
import { type UILiveSessionReportChapter } from "../../../ports/primary/ui/types"
import {
    validatedWorkLabel,
    todoWorkLabel,
    activityLevelLabel,
    activityLevelVariant,
    chapterProgressFlagColor,
    chapterMasteryBar,
} from "../../../ports/primary/ui/formatters/liveSessionReportFormatter"

export type ReportChapterRowProps = {
    chapter: UILiveSessionReportChapter
    testID?: string
}

export function ReportChapterRow(props: ReportChapterRowProps) {
    const { chapter, testID } = props
    const { chapter: c, validatedCount, todoCount, xpGained, xpTotal, xpToValidate, level } = chapter
    const { subject } = c

    const bar = xpGained != null && xpTotal != null ? chapterMasteryBar(xpGained, xpTotal, xpToValidate) : null

    return (
        <Stack gap="2" testID={testID}>
            <Row gap="3" align="center">
                <ImageIcon uri={subject.iconUrl} size={32} />
                <Stack gap="1" style={styles.shrink}>
                    <Text weight="semibold" color={subject.color}>
                        {c.name}
                    </Text>
                    <Text size="text-sm" color="text-primary" weight="semibold">
                        {subject.name}
                    </Text>
                    {level != null && (
                        <Badge
                            text={activityLevelLabel(level)}
                            variant={activityLevelVariant(level)}
                            iconName="signal"
                        />
                    )}
                </Stack>
            </Row>

            {validatedCount > 0 && (
                <Banner
                    icon="check-circle"
                    color="success"
                    align="start"
                    message={validatedWorkLabel(validatedCount)}
                />
            )}
            {todoCount > 0 && (
                <Banner icon="hourglass-half" color="accent" align="start" message={todoWorkLabel(todoCount)} />
            )}

            {bar != null && (
                <FlagProgressBar
                    value={bar.value}
                    flagAt={bar.flagAt}
                    color="success"
                    flagColor={chapterProgressFlagColor(todoCount)}
                    label={
                        <Text size="text-md" weight="semibold">
                            {xpGained}
                            <Text size="text-sm" color="text-muted">{` / ${xpTotal}`}</Text>
                        </Text>
                    }
                />
            )}
        </Stack>
    )
}

const styles = StyleSheet.create(() => ({
    shrink: {
        flexShrink: 1,
    },
}))
